import * as React from 'react';
import { useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import FormikValidatorService from '../../../Services/FormikValidatorService';
import Form from './Form';
import ForgotPasswordService from '../ForgotPasswordService';
import { defaultLanguage } from '../../LanguagesContext';
import LoginInput from '../../Forms/ReactHookFormFields/LoginInput';
import { transformErrors } from '../../../Services/HookFormService';
import useAnalytics from '../../../hooks/useAnalytics';

const RESPONSE_OK = 'ok';
const RESPONSE_WARNING = 'warning';

interface ForgotPasswordLayoutProps {
  data: any;
  resetPasswordToken?: string;
  resetPasswordCallback?: () => void;
  email?: string;
}

export default function ForgotPasswordLayout(props: ForgotPasswordLayoutProps) {
  const { trackEvent } = useAnalytics();
  const { formatMessage: f } = useIntl();
  const [isButtonLoaderActive, setIsButtonLoaderActive] = React.useState<boolean>(false);
  // const [isDone, setIsDone] = React.useState<boolean>(false);
  const [alert, setAlert] = React.useState<any>(null);

  const validateFields = (values: { email: string }) => {
    let formErrors = {};
    const emailData = props.data.find((i: any) => i.indexId === 'email');
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, {
      email: emailData && emailData.rules ? emailData.rules : [],
    });

    return transformErrors(formErrors);
  };

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm<{ email: string }>({
    mode: 'onChange',
    defaultValues: {
      email: props.email || '',
    },
    resolver: async (data: { email: string }) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const onSubmit = async (values: { email: string }) => {
    setIsButtonLoaderActive(true);

    if (props.resetPasswordToken) {
      const response = await ForgotPasswordService.submitFormForExpiredToken(
        values.email,
        props.resetPasswordToken,
        defaultLanguage
      );
      if (response && response.status === RESPONSE_OK) {
        trackEvent(['Forgot password', 'submit', 'success']);
        // setIsDone(true);
        if (props.resetPasswordCallback) {
          props.resetPasswordCallback();
        }
        // setValue('email', '');
        // reset();
      } else if (response && response.status === RESPONSE_WARNING) {
        trackEvent(['Forgot password', 'submit', response.toast.message])
        setAlert({
          title: f({ id: 'toastr.warning' }),
          message: f({ id: response.toast.message }),
          type: 'warning',
        });
      }
    } else {
      const response = await ForgotPasswordService.submitForm(values.email, defaultLanguage);
      if (response && response.status === RESPONSE_OK) {
        trackEvent(['Forgot password', 'submit', 'success']);
        // setIsDone(true);
        setAlert({
          title: f({ id: 'toastr.info' }),
          message: f({ id: response.toast.message }),
          type: 'info',
        });
        // setValue('email', '');
        // reset();
      } else if (response && response.status === RESPONSE_WARNING) {
        trackEvent(['Forgot password', 'submit', response.toast.message]);
        setAlert({
          title: f({ id: 'toastr.info' }),
          message: f({ id: response.toast.message }),
          type: 'info',
        });
      }
    }
    setIsButtonLoaderActive(false);
  };

  return (
    <Form
      id="forgotpassword-form"
      save={handleSubmit(onSubmit)}
      isButtonLoaderActive={isButtonLoaderActive}
      isDone={false}
      alert={alert}
      email={watch('email')}
      isValid={!errors.email}
    >
      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <LoginInput
            {...field}
            id="email"
            label="Email"
            type="email"
            onChangeCallback={(value) => {
              setValue('email', value);
              trigger('email');
            }}
            error={errors.email?.message}
          />
        )}
      />
    </Form>
  );
}
